import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, AButton, Skeleton, EmptyState } from "@/components/ios";
import { CalendarRange, MapPin, Route, Clock, Check, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Scheduling() {
  const [due, setDue] = useState(null);
  const [selected, setSelected] = useState([]);
  const [route, setRoute] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/schedule/due").then(({ data }) => setDue(data)); }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const optimize = async () => {
    if (selected.length < 2) { toast.error("Select at least 2 properties"); return; }
    setBusy(true);
    try { const { data } = await api.post("/schedule/optimize", { property_ids: selected }); setRoute(data); }
    catch (e) { toast.error("Optimisation failed"); } finally { setBusy(false); }
  };

  const saveRun = async () => {
    await api.post("/schedule/runs", { date: new Date().toISOString().slice(0, 10), property_ids: selected, route });
    toast.success("Day run saved · offline data pre-fetched", { icon: <CalendarPlus size={16} /> });
  };

  const downloadIcs = () => {
    const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//HarbourCity//Inspections//EN",
      ...route.route.flatMap((r, i) => ["BEGIN:VEVENT", `SUMMARY:Inspection - ${r.property.address}`,
        `DESCRIPTION:Stop ${i + 1}, ${r.leg_km}km leg`, `LOCATION:${r.property.suburb}`, "END:VEVENT"]),
      "END:VCALENDAR"].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "inspection-run.ics"; a.click();
  };

  return (
    <div>
      <LargeTitle subtitle="Cluster due properties into an optimised day route">Scheduling</LargeTitle>
      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2 px-1">Due & overdue · select stops</div>
          {!due ? <div className="space-y-3">{[0,1,2].map(i => <Skeleton key={i} className="h-16" />)}</div>
            : due.length === 0 ? <Surface><EmptyState icon={CalendarRange} title="Nothing due" subtitle="No properties currently need inspecting." /></Surface>
            : <div className="space-y-2">
              {due.map((p) => {
                const on = selected.includes(p.id);
                return (
                  <Surface key={p.id} className="p-4 flex items-center gap-3 cursor-pointer" data-testid={`sched-prop-${p.id}`} onClick={() => toggle(p.id)}>
                    <div className="h-6 w-6 rounded-full grid place-items-center border-2 transition-colors" style={{ borderColor: on ? "var(--accent)" : "var(--border)", background: on ? "var(--accent)" : "transparent" }}>
                      {on && <Check size={14} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate" style={{ color: "var(--text)" }}>{p.address}</div>
                      <div className="text-sm text-secondary flex items-center gap-1"><MapPin size={12} /> {p.suburb}</div>
                    </div>
                    <Pill tone={p.due_status === "overdue" ? "danger" : "warning"}>{p.due_status.replace("_", " ")}</Pill>
                  </Surface>
                );
              })}
              <AButton testid="optimize-route" className="w-full mt-2" onClick={optimize} disabled={busy}><Route size={15} className="inline mr-1.5" />{busy ? "Optimising…" : `Optimise route (${selected.length})`}</AButton>
            </div>}
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2 px-1">Optimised run</div>
          {!route ? <Surface><EmptyState icon={Route} title="No route yet" subtitle="Select stops and optimise to sequence your day with travel estimates." /></Surface>
            : <Surface className="p-5">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["Stops", route.stops], ["Distance", `${route.total_km}km`], ["Est. time", `${Math.floor(route.est_total_minutes / 60)}h ${route.est_total_minutes % 60}m`]].map(([l, v]) => (
                  <div key={l} className="text-center"><div className="text-xl font-bold" style={{ color: "var(--text)" }}>{v}</div><div className="text-xs text-secondary">{l}</div></div>
                ))}
              </div>
              <div className="space-y-2">
                {route.route.map((r, i) => (
                  <motion.div key={r.property.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--surface-2)" }}>
                    <div className="h-7 w-7 rounded-full grid place-items-center text-white text-xs font-bold shrink-0 accent-bg">{i + 1}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{r.property.address}</div><div className="text-xs text-secondary">{r.property.suburb}</div></div>
                    <span className="text-xs text-secondary flex items-center gap-1"><Clock size={11} /> {r.leg_km}km</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <AButton testid="save-run" className="flex-1" onClick={saveRun}>Save day run</AButton>
                <AButton variant="text" onClick={downloadIcs}>Export .ics</AButton>
              </div>
            </Surface>}
        </div>
      </div>
    </div>
  );
}
